import { Injectable, computed, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import type {
  GrTableFilterSelection,
  GrTableLazyLoadEvent,
  GrTableSortOrder,
} from '@components/table';

import { UserSettingsService } from './user-settings.service';

export type GrTableUrlState = Partial<GrTableLazyLoadEvent>;

const GROUP_SEPARATOR = ';';
const KEY_SEPARATOR = ':';
const VALUE_SEPARATOR = ',';

@Injectable({ providedIn: 'root' })
export class TableStateService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly settings = inject(UserSettingsService);

  /**
   * Rows per page for a full-page listing whose URL does not say. The URL still wins, so a shared
   * link opens at the size it was shared with.
   */
  readonly defaultRows = computed<number>(() => this.settings.appearance().pageSize);

  read(prefix = ''): GrTableUrlState {
    const params = this.route.snapshot.queryParamMap;
    const key = (name: string) => (prefix ? `${prefix}_${name}` : name);

    const firstRaw = params.get(key('first'));
    const rowsRaw = params.get(key('rows'));
    const sortField = params.get(key('sortField'));
    const sortOrderRaw = params.get(key('sortOrder'));
    const search = params.get(key('search')) ?? '';

    const first = firstRaw !== null ? Number(firstRaw) : 0;
    const rows = rowsRaw !== null ? Number(rowsRaw) : undefined;
    const sortOrder: GrTableSortOrder | null =
      sortOrderRaw === 'asc' || sortOrderRaw === 'desc' ? sortOrderRaw : null;

    return {
      first: Number.isFinite(first) && first >= 0 ? first : 0,
      rows: Number.isFinite(rows) && (rows as number) > 0 ? (rows as number) : undefined,
      sortField: sortField || null,
      sortOrder,
      search,
      filters: this.decodeFilters(params.get(key('filters'))),
    };
  }

  write(state: GrTableUrlState, prefix = ''): void {
    const key = (name: string) => (prefix ? `${prefix}_${name}` : name);

    const queryParams: Params = {
      [key('first')]: state.first && state.first > 0 ? state.first : null,
      [key('rows')]: state.rows ?? null,
      [key('sortField')]: state.sortField ?? null,
      [key('sortOrder')]: state.sortOrder ?? null,
      [key('search')]: state.search ? state.search : null,
      [key('filters')]: this.encodeFilters(state.filters),
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  clear(prefix = ''): void {
    this.write(
      { first: 0, rows: undefined, sortField: null, sortOrder: null, search: '', filters: {} },
      prefix,
    );
  }

  private encodeFilters(filters: GrTableFilterSelection | undefined): string | null {
    if (!filters) return null;
    const parts = Object.entries(filters)
      .filter(([, values]) => values?.length)
      .map(([group, values]) => `${group}${KEY_SEPARATOR}${values.join(VALUE_SEPARATOR)}`);
    return parts.length ? parts.join(GROUP_SEPARATOR) : null;
  }

  private decodeFilters(raw: string | null): GrTableFilterSelection {
    if (!raw) return {};
    const filters: GrTableFilterSelection = {};
    for (const part of raw.split(GROUP_SEPARATOR)) {
      const index = part.indexOf(KEY_SEPARATOR);
      if (index <= 0) continue;
      const group = part.slice(0, index);
      const values = part
        .slice(index + 1)
        .split(VALUE_SEPARATOR)
        .filter((value) => value.length > 0);
      if (values.length) filters[group] = values;
    }
    return filters;
  }
}
